import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DealStatusMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/deal-status-multi-select/deal-status-multi-select.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { DEAL_STATUS_DEFAULT_COLOR, DealStatusFormDto, type DealStatusFormInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-form.dto';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { DealStatusService } from '@zaoblako/web/core/services/deal/deal-status.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import type { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import type { SerdeDtoMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-metadata.interface';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ChangeDetectorRef,
  type OnInit
} from '@angular/core';
import type { Observable } from 'rxjs';

type StatusFormExtended = DealStatusFormInterface & { id?: number; };

@Component({
  standalone: true,
  selector: 'crm-deal-status-form',
  templateUrl: './deal-status-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SpinnerComponent,
    ButtonComponent,
    DealStatusMultiSelectComponent,
    ColorPickerComponent,
    InputTextComponent,
    StatusLabelComponent,
    JoinPipe,
    CommonModule,
    FormsModule,
    CardModule,
    CheckboxModule,
    TooltipModule,
    DividerModule
  ]
})
export class DealStatusFormComponent extends BaseFormComponent<StatusFormExtended> implements OnInit {
  /** Form index */
  @Input() public readonly index: number;

  /** Status DTO metadata */
  public readonly metadata: SerdeDtoMetadataInterface<DealStatusFormInterface> = DealStatusFormDto.getMetadata(undefined, 1);

  /** Status form */
  public status: StatusFormExtended = {
    name: '',
    color: DEAL_STATUS_DEFAULT_COLOR
  };

  /** Status editable flag */
  public editable: boolean = true;

  /** Status soft deletion timestamp */
  public deleted: boolean = false;

  /** Status DTO schema */
  public dto = DealStatusFormDto;

  /** Form base state observer */
  public form$: Observable<FormType>;

  /** Form loading state observer */
  public loading$: Observable<boolean>;

  /** Status exclude IDs observer */
  public withoutIds$: Observable<number[]>;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly dealStatusService: DealStatusService,
    private readonly errorService: ErrorService,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);
    this.withoutIds$ = this.formStoreService.select(
      (state) => state?.forms?.[this.index]?.entityId
        ? [state.forms[this.index].entityId]
        : []
    );

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.loadStatus();
    }
  }

  private loadStatus(): void {
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    if (!entityId) {
      return;
    }

    this.formStoreService.setLoading(true, this.index);
    this.dealStatusService
      .findById<DealStatusFindInterface>(entityId, { withDeleted: true })
      .subscribe({
        next: (status) => {
          this.formStoreService.setLoading(false, this.index);
          this.status = {
            ...DealStatusFormDto.deserialize(status),
            id: status.id
          };
          this.deleted = !!status.deleted,
          this.editable = status.editable ?? true;
          this.errors = null;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public saveStatus(): void {
    const validationResult = this.checkValidate(this.status);
    if (!validationResult.valid) {
      return;
    };

    this.formStoreService.setLoading(true, this.index);

    const saveRequest$ = this.status.id
      ? this.dealStatusService.update<StatusFormExtended, DealStatusFindInterface>(this.status)
      : this.dealStatusService.create<DealStatusFindInterface>(this.status);

    saveRequest$.subscribe({
      next: (status) => {
        this.formStoreService.setLoading(false, this.index);
        this.errorService.addSuccessMessage(
          this.status.id ? `Статус сделки "${status.name}" обновлен` : 'Статус сделки создан'
        );
        this.formStoreService.saveNext(status, this.index);
      },
      error: () => {
        this.formStoreService.setLoading(false, this.index);
      }
    });
  }

  public deleteStatus(): void {
    if (!this.status?.id) {
      return;
    }

    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить статус сделки "${this.status.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.changeDetectorRef.markForCheck();

        this.dealStatusService.deleteById<DealStatusFindInterface>(this.status.id).subscribe({
          next: (status) => {
            this.errorService.addSuccessMessage(`Статус сделки "${status.name}" удален`);
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
            this.changeDetectorRef.markForCheck();
          }
        });
      }
    });
  }

  public restoreStatus(): void {
    if (!this.status?.id) {
      return;
    }

    this.confirmDialogService.confirm({
      message: `Вы действительно хотите восстановить статус сделки "${this.status.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.changeDetectorRef.markForCheck();

        this.dealStatusService.restoreOne(this.status.id).subscribe({
          next: (status) => {
            this.errorService.addSuccessMessage(`Статус сделки "${status.name}" восстановлен`);
            this.formStoreService.saveNext(status, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
            this.changeDetectorRef.markForCheck();
          }
        });
      }
    });
  }
}
